import { ENTER } from '@angular/cdk/keycodes';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter, Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
// tslint:disable-next-line:import-blacklist
import { UntypedFormControl, UntypedFormGroup, NgForm, ValidationErrors, Validators, FormControl } from '@angular/forms';
import { MatLegacyAutocomplete as MatAutocomplete, MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent, MatLegacyAutocompleteTrigger as MatAutocompleteTrigger } from '@angular/material/legacy-autocomplete';
import { forkJoin, Observable } from 'rxjs';
import {debounceTime, finalize, map, skipWhile, switchMap, takeUntil, filter} from 'rxjs/operators';
import {cloneDeep, difference, find, isString, remove } from 'lodash-es';

import {
  FindControlAdditionalSearchParametersDto,
  FindControlRequiredSearchParametersDto,
  FindControlResultDto,
  FindControlInitDto,
  GenericSimpleChanges,
  GetDataSearchDto,
  IColumnPk,
  throwAsyncError,
  PrepopulateType,
  HiddenActionItemType,
} from 'webcommon/shared';
import { ApBaseComponent } from 'webcommon/core-ui';
import { ApFindControlModalService } from './ap-findcontrol-modal.service';
import { ApFindControlService } from './ap-findcontrol.service';
import { FindControlMode } from '../models/index';

// TODO move this out
interface AutocompleteGroup {
  AdditionalRows: boolean;
  isCollapsable: boolean;
  isCollapsed: boolean;
  GroupDisabled: boolean;
  Name: string;
  ItemList: IColumnPk[];
}

@Component({
  selector: 'ap-findcontrol',
  templateUrl: './ap-findcontrol.component.html',
})
export class ApFindControlComponent extends ApBaseComponent implements OnInit, OnChanges, Validators, AfterViewInit, AfterViewChecked  {
  @Input() baseId: string;
  @Input() findControlId: string;
  @Input() placeHolder: string;
  @Input() disabled: boolean;
  @Input() findControlDisplayName: string;
  @Input() requiredSearchParameters: FindControlRequiredSearchParametersDto | null = null;
  @Input() additionalSearchParameters: FindControlAdditionalSearchParametersDto | null = null;
  @Input() singleSelect = false;
  @Input() required?: boolean;
  @Input() form?: NgForm;
  @Input() formGroup?: UntypedFormGroup;
  @Input() controlName?: string;
  @Input() showRequiredOnly = false;
  @Input() autoFocus = false;
  @Input() hiddenActionItems?: HiddenActionItemType[];

  @Output() dirty = new EventEmitter<boolean>();

  // Really, we should use either selectedPks or selectedPk, but not both.
  @Output() selectedPksChange = new EventEmitter<string[]>();
  private _selectedPks: string[] = [];
  @Input()
  get selectedPks() {
    return this._selectedPks;
  }
  set selectedPks(val) {
    // Only emit events and update the internal value if the value changes.
    if (this._selectedPks !== val) {
      this._selectedPks = val;

      if (!val) {
        throwInvalidValue('selectedPks', val);
      }
      // set the other property, but not thru the setter, so we don't create a loop
      // there are a few places where this value may still be set to null or undefined,
      // so we need this '?.' conditional check for now, until those places are resolved
      const firstPk = val?.[0] || null;
      this._selectedPk = firstPk;
    }
  }

  @Output() selectedPkChange = new EventEmitter<string | null>();
  private _selectedPk: string | null = null;
  @Input()
  get selectedPk() {
    return this._selectedPk;
  }
  set selectedPk(val) {
    const normalizedVal = val || null;

    // Only emit events and update the internal value if the value changes.
    if (this._selectedPk !== normalizedVal) {
      this._selectedPk = normalizedVal;

      // set the other property, but not thru the setter, so we don't create a loop
      const newSelectedPks = normalizedVal ? [normalizedVal] : [];
      this._selectedPks = newSelectedPks;
    }
  }

  @Output() chipsChange = new EventEmitter<IColumnPk[]>();

  // useful for singleSelect = true
  @Output() chipChange = new EventEmitter<IColumnPk | null>();

  @ViewChild(MatAutocompleteTrigger, { static: true }) autocomplete: MatAutocompleteTrigger;

  Id: string;
  visible = true;
  selectable = true;
  removable = true;
  addOnBlur = false;
  // The value can actually be the [value] of an option right after it's selected, not just string | null.
  autoCompleteFormControl = new FormControl<string | null | IColumnPk>(null);
  findControlDto?: FindControlInitDto;
  chips: IColumnPk[] = [];
  // this can hold a reference to the selectedPks that are directly tied to these chips,
  // so that we have something to compare to tell if the selectedPks changed
  private chipsPksSource: string[] = [];

  loadingConfig = false;  // loading find control config
  loadingItems = false;   // loading selected chip items
  loadingData = false;    // loading search results
  autoCompleteOpen = false;
  updatingmfumru = false;
  focused: boolean;

  readonly separatorKeysCodes: number[] = [ENTER];
  readonly waitTime = 300;
  readonly MFU_MAXCOUNT = 5;
  readonly MRU_MAXCOUNT = 5;

  autocompleteGroups$: Observable<AutocompleteGroup[]>;
  findControlFormControl?: UntypedFormControl;

  @ViewChild('chipInput', { static: true }) chipInput: ElementRef<HTMLInputElement>;
  @ViewChild('auto', { static: true }) matAutocomplete: MatAutocomplete;

  @ViewChild('openModalButton', { static: true, read: ElementRef })
  openModalButton: ElementRef<HTMLElement>;

  constructor(
    private apFindControlModalService: ApFindControlModalService,
    private apFindControlService: ApFindControlService,
    private changeDetector: ChangeDetectorRef,
    private elementInjector: Injector,
  ) {
    super();
  }

  ngOnInit() {
    this.setId();
    this.initializeFormControl();
    this.load();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.setAutofocus();
    });
  }

  load() {
    this.loadingConfig = true;
    this.apFindControlService.getConfig(this.findControlId).pipe(
      finalize(() => {
        this.loadingConfig = false;
      }),
      takeUntil(this.onDestroy$),
    ).subscribe(
      (data) => this.onLoadConfigSuccess(data),
      () => this.onError(),
    );
  }

  initializeFormControl() {
    if (!this.form) { return; } // if no form is given do not set this up

    this.findControlFormControl = new UntypedFormControl(null, this.validate.bind(this));

    if (this.formGroup) {
      this.formGroup.addControl(this.controlName ? this.controlName : this.Id, this.findControlFormControl);
      this.updateFormControl();
    } else {
      this.form.form.addControl(this.Id, this.findControlFormControl);
      this.updateFormControl();
    }
  }

  private updateFormControl() {
    if (!this.findControlFormControl) { return; }
    this.findControlFormControl.setValue(this.validate());
  }

  validate(): ValidationErrors | null {
    if (!this.chips.length) {
      if (this.isShowRequriedOnly()) return null;
    }
    const valid = this.getIsValid();
    return valid ? null : {
      required: true,
    };
  }

  private getIsValid(): boolean {
    const isValid = !this.required || !!(this.chips.length);
    return isValid;
  }

  isRequired(): boolean {
    const required = !this.required ? false : this.required;
    return !this.isShowRequriedOnly() && required;
  }

  isShowRequriedOnly(): boolean {
    return this.showRequiredOnly;
  }

  ngOnChanges(changes: GenericSimpleChanges<ApFindControlComponent>) {
    if (changes.baseId) {
      this.setId();
    }

    if (changes.disabled) {
      if (changes.disabled.currentValue) {
        this.autoCompleteFormControl.disable();
      } else {
        this.autoCompleteFormControl.enable();
      }
    }

    if (changes.selectedPks && changes.selectedPk) {
      throw new Error('ApFindControlComponent: Don\'t use selectedPk, and selectedPks together.');
    }

    if ((changes.selectedPks || changes.selectedPk)
        && this.findControlDto
    ) { // should be initialized at this point
      const selectedPks = changes.selectedPks?.currentValue ?? this.selectedPks;
      this.updateSelectedChips(selectedPks);
    }

    if (changes.required) {
      this.updateFormControl();
    }
  }

  onApplyDatePickerStyle(): any {
    const obj: any = {};
    const required = !this.required ? false : this.required;
    if (required || this.isShowRequriedOnly()) {
      obj.backgroundColor = '#fafad2';
    }
    return obj;
  }

  private searchSetup() {
    // TODO: update type here after angular 14 upgrades and other places with form controls to be like this
    this.autocompleteGroups$ = this.autoCompleteFormControl.valueChanges.pipe(
      debounceTime(this.waitTime),
      filter((val): val is string | null => {
        return !val || isString(val);
      }),
      skipWhile(() => this.loadingConfig || this.loadingItems), // Don't load info while loading
      map((val) => this.createSearchDto(val)),
      switchMap((searchDto) => this.getSearchResults(searchDto)),
      map((autoCompleteGroups) => this.removeCurrentlySelectedFromData(autoCompleteGroups)),
      map((autocompleteGroups) => this.prependCurrentlySelected(autocompleteGroups)),
    );
  }

  private createSearchDto(searchString: string | null): GetDataSearchDto {
    const searchDto: GetDataSearchDto = {
      RequiredSearchParameters: this.requiredSearchParameters ?? null,
      AdditionalSearchParameters: this.additionalSearchParameters ?? null,
      SearchText: searchString || '',
      ShowInactive: false,
    };
    return searchDto;
  }

  private prependCurrentlySelected(autocompleteGroup: AutocompleteGroup[]): AutocompleteGroup[] {
    const chipList = this.chips.length ? cloneDeep(this.chips) : [];
    const chipGroup: AutocompleteGroup = {
      AdditionalRows: false,
      GroupDisabled: true,
      ItemList: chipList,
      Name: 'Currently Selected',
      isCollapsable: true,
      isCollapsed: true,
    };
    autocompleteGroup.unshift(chipGroup);
    return autocompleteGroup;
  }

  private removeCurrentlySelectedFromData(autocompleteGroup: AutocompleteGroup[]): AutocompleteGroup[] {
    if (!autocompleteGroup) {
      return [];
    }

    // there are no currently selected items to filter out
    if (!this.chips.length) {
      return autocompleteGroup;
    }

    const currentPks = this.chips.map((chip) => chip.Pk);

    autocompleteGroup.forEach((data) => {
      remove(data.ItemList, (PkItem) => find(currentPks, (cPk) => cPk.toLowerCase() === PkItem.Pk.toLowerCase()));
    });
    return autocompleteGroup;
  }

  private getSearchResults(searchDto: GetDataSearchDto): Observable<AutocompleteGroup[]> {
    this.loadingData = true;

    let dataArray: Observable<AutocompleteGroup[]>;

    if (searchDto.SearchText && searchDto.SearchText.length >= (this.findControlDto?.FindControlBehavior?.MinimumSearchCharacters ?? 0)) {
      dataArray = forkJoin(
        [
          this.apFindControlService.getData(this.findControlId, searchDto).pipe(
            map((data) => this.formatResults(data, this.formatResultListName(data))),
          )
        ],
      );
    } else if (this.findControlDto?.FindControlBehavior?.Prepopulate === PrepopulateType.MRU) {
      dataArray = forkJoin(
        [
          this.apFindControlService.getMruList(this.findControlId, this.MRU_MAXCOUNT, searchDto).pipe(
            map((data) => this.formatResults(data, 'Most Recently Used')),
          )
        ],
      );
    } else if (this.findControlDto?.FindControlBehavior?.Prepopulate === PrepopulateType.MFU_Frequency ||
              this.findControlDto?.FindControlBehavior?.Prepopulate === PrepopulateType.MFU_Alpha) {
      dataArray = forkJoin(
        [
          this.apFindControlService.getMfuList(this.findControlId, this.MFU_MAXCOUNT, searchDto).pipe(
            map((data) => this.formatResults(data, 'Most Frequently Used')),
          )
        ],
      );
    }
    else { // prepopulation is all or none
      dataArray = forkJoin(
        [
          this.apFindControlService.getData(this.findControlId, searchDto).pipe(
            map((data) => this.formatResults(data, 'All')),
          )
        ],
      );
    }

    return dataArray.pipe(
      finalize(() => {
        this.loadingData = false;
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private formatResults(findResults: FindControlResultDto,  name: string): AutocompleteGroup {
    const group: AutocompleteGroup = {
      AdditionalRows: findResults.AdditionalRows,
      GroupDisabled: false,
      ItemList: findResults.Rows,
      Name: name,
      isCollapsable: false,
      isCollapsed: false,
    };

    return group;
  }

  private formatResultListName(results: FindControlResultDto): string {
    const name = 'Results (' + results.Rows.length + (results.AdditionalRows ? ')' : '+)');
    return name;
  }

  openModal() {
    if (this.disabled) { return; }

    this.autoCompleteFormControl.disable();
    this.autocomplete.closePanel();
    const chipsCopy = cloneDeep(this.chips);
    this.apFindControlModalService.openModal(
      this.elementInjector,
      this.findControlId,
      chipsCopy,
      this.Id,
      this.findControlDisplayName,
      this.singleSelect ? FindControlMode.SingleSelect : FindControlMode.MultiSelect,
      this.requiredSearchParameters ?? undefined,
      this.additionalSearchParameters ?? undefined,
      this.hiddenActionItems ?? undefined,
    ).pipe(
      finalize(() => this.autoCompleteFormControl.enable()),
      this.takeUntilDestroy(),
    ).subscribe(
      (result) => {
        if (result.isSuccess) {
          this.chips = result.resultValue;
          this.chipsChanged(this.chips);
        }
      }
    );
  }

  private onLoadConfigSuccess(data: FindControlInitDto) {
    this.findControlDto = data;
    this.updateSelectedChips(this.selectedPks);
    this.searchSetup();
  }

  private updateSelectedChips(selectedPks: string[] | null) {
    // if selectedPks === chipsPksSource,
    // then the chips and selectedPks are already in sync, so we don't need to update
    if (selectedPks === this.chipsPksSource) {
      return;
    }

    // there are a few places where this value may still be set to null or undefined,
    // so we need this '?.' conditional check for now, until those places are resolved
    if (!selectedPks?.length) {
      // If list is null or empty no need to call endpoint
      this.chips = [];
      this.chipsChanged(this.chips);
    } else  {
      this.loadSelectedItems(selectedPks);
    }
  }

  private loadSelectedItems(selectedPks: string[]) {
    this.loadingItems = true;
    this.apFindControlService.getSelectedItems(this.findControlId, selectedPks).pipe(
      finalize(() => {
        this.loadingItems = false;
      }),
      takeUntil(this.onDestroy$),
    ).subscribe(
      (data) => this.onLoadSelectedItemsSuccess(data),
      () => this.onError(),
    );
  }

  private onLoadSelectedItemsSuccess(resultDto: FindControlResultDto) {
    this.chips = resultDto.Rows;
    this.chipsChanged(this.chips);
  }

  private onError() {
    // TODO implement
  }

  getDisplayName(data: IColumnPk): string {
    return data[this.findControlDto?.FindControlBehavior?.ColumnsConfig.PrimaryDisplayName ?? ''];
  }

  getChipDisplayName(data: IColumnPk): string {
    return this.getDisplayName(data);
  }

  getAutocompleteDisplayName(data: IColumnPk): string {
    return this.getDisplayName(data);
  }

  removeChip(object: IColumnPk): void {
    const index = this.chips.map((chip) => chip.Pk).indexOf(object.Pk);

    if (index >= 0) {
      this.chips.splice(index, 1);
      this.chipsChanged(this.chips);
    }
  }

  // TODO look into click vs enter - different logic occurs
  chipSelectedFromAutoComplete(event: MatAutocompleteSelectedEvent): void {
    const value: IColumnPk | undefined = event.option.value;
    if (!value) { return; }

    if (this.singleSelect) {
      this.chips = [value];
      this.chipsChanged(this.chips);
    } else {
      if (!this.chips.find((chip) => chip.Pk === value.Pk)) {
        this.chips.push(value);
        this.chipsChanged(this.chips);
      }
    }
    this.chipInput.nativeElement.value = '';
    this.autoCompleteFormControl.setValue(null);
  }

  onAutoCompleteOpen() {
    this.autoCompleteOpen = true;
  }

  onAutoCompleteClose() {
    this.autoCompleteOpen = false;
  }

  onMfuMruSuccess(sentUids: string[], returnedUids: string[]) {
    const comparison = difference(sentUids, returnedUids);
    if (comparison.length !== 0) {
      // something happened where the MFU/MRU was not updated for all sent uids
      // this.onError();
    }
  }

  // Call this after changing this.chips
  protected chipsChanged(chips: IColumnPk[]): void {
    const chipPks = chips.map((chip) => chip.Pk);
    // there are a few places where this value may still be set to null or undefined,
    // so we need this default value for now, until those places are resolved
    const selectedPks = this.selectedPks || [];
    const diffList = difference(chipPks, selectedPks);

    if (diffList.length > 0) {
      this.updatingmfumru = true;
      this.apFindControlService.updateMfuMruList(this.findControlId, diffList).pipe(
        finalize(() => {
          this.updatingmfumru = false;
        }),
        takeUntil(this.onDestroy$),
      ).subscribe(
        (data) => this.onMfuMruSuccess(diffList, data),
        () => this.onError(), // TODO We don't really care if this fails, maybe remove this
      );
    }

    // if the selectedPks are out of sync with chips, then update it
    if (diffList.length > 0 || chipPks.length !== selectedPks.length) {
      this.chipsPksSource = chipPks;
      this.selectedPks = chipPks;
      this.selectedPksChange.emit(chipPks);
      this.selectedPkChange.emit(this.selectedPk);
      // TODO only fire when changed
      this.dirty.emit(true);
      this.findControlFormControl?.markAsDirty();
    }
    this.updateFormControl();
    this.chipsChange.emit(chips);
    this.chipChange.emit(chips[0] || null);
  }

  ngAfterViewChecked() {
    this.changeDetector.detectChanges();
  }

  private setId() {
    this.Id = `${this.baseId}-findcontrol`;
  }

  toggleCollapsed(event: Event, group: AutocompleteGroup) {
    event.stopPropagation();
    event.preventDefault();
    group.isCollapsed = !group.isCollapsed;
  }

  inputFocused() {
    if (!this.loadingConfig && !this.loadingItems) {
      // I think this is currently necessary, because if the chips, or requiredSearchParameters, or additionalSearchParameters
      // have changed since the last time this was focused, then it can redo the autocomplete group list
      // to match these values.
      // Also, this will get the list loaded the first time.
      this.autoCompleteFormControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
    }

    this.focused = true;
  }

  inputBlurred(): void {
    this.focused = false;
  }

  // The current purpose of this mainly is so that when you click on a chip
  // or something in the mat-form-field that is not the actual input box, it will still focus the input box,
  // and allow you to type.
  findControlClicked(event?: Event) {
    // don't focus the input if click came from modal button
    if (event?.target instanceof Node && this.openModalButton.nativeElement.contains(event.target)) {
      return;
    }

    this.tryFocusInput();
  }

  shouldExpand(): boolean {
    const hasChips = this.selectedPks?.length > 0;
    const isFocused = this.focused || this.autoCompleteOpen;

    const shouldExpand = (isFocused) || (!isFocused && !hasChips);

    return shouldExpand;
  }

  setAutofocus() {
    if (this.autoFocus) {
      this.tryFocusInput();
    }
  }

  tryFocusInput() {
    if (this.disabled) {
      return;
    }

    if (!this.chipInput) {
      return;
    }
    // Focus the input box and open the autocomplete
    this.chipInput.nativeElement.focus();
  }
}

function throwInvalidValue(propertyName: string, value: any): void {
  throwAsyncError(new Error(`'${propertyName}' is set to an invalid value based on its type,` +
    ` which is cheating the type system; value: ${value}`
  ));
}
